import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { MEMBER_APPLICATIONS_ENABLED } from "@/features/memberApplications/flags";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { RoleBadge } from "@/components/members/RoleBadge";
import { MemberRole, getRoleDisplayName, getRoleDescription, logAuditAction } from "@/lib/permissions";
import { Trash2, Edit2, Mail, CheckCircle2, Shield } from "lucide-react";
import { format } from "date-fns";
import MainLayout from "@/components/layout/MainLayout";
import { useMobile } from "@/hooks/useMobile";

interface UnaMember {
  id: string;
  una_id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  role: string;
  status: string;
  joined_date: string | null;
  user_id: string | null;
  member_role: MemberRole;
  last_login: string | null;
  login_count: number;
  onboarded_via_workflow: boolean;
}

interface MemberInvitation {
  id: string;
  una_member_id: string;
  status: string;
  expires_at: string;
  created_at: string;
}

export default function UNAMembersUpdated() {
  const { user } = useAuth();
  const { toast } = useToast();
  const isMobile = useMobile();
  const [members, setMembers] = useState<UnaMember[]>([]);
  const [invitations, setInvitations] = useState<Map<string, MemberInvitation>>(new Map());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<UnaMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [unaId, setUnaId] = useState<string>();
  const [unaName, setUnaName] = useState<string>();

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    role: "",
    status: "active",
    member_role: "board_member_passive" as MemberRole,
  });

  useEffect(() => {
    if (user) {
      loadUnaAndMembers();
    }
  }, [user]);

  const loadUnaAndMembers = async () => {
    try {
      const { data: una, error: unaError } = await supabase
        .from("unas")
        .select("id, name")
        .eq("treasury_secretary_id", user?.id)
        .single();

      if (unaError) throw unaError;

      if (una) {
        setUnaId(una.id);
        setUnaName(una.name);
        await loadMembers(una.id);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "No UNA found. Please set up your UNA first.",
        });
        setLoading(false);
      }
    } catch (error) {
      console.error("Error loading UNA:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load UNA",
      });
      setLoading(false);
    }
  };

  const loadMembers = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from("una_members")
        .select("*")
        .eq("una_id", id)
        .order("joined_date", { ascending: false });

      if (error) throw error;
      setMembers(data || []);

      // Load invitations for all members (via backend function to avoid RLS edge-cases)
      const { data: invResp, error: invErr } = await supabase.functions.invoke('list-member-invitations', {
        body: { una_id: id }
      });

      if (!invErr && invResp?.invitations) {
        const invitationsMap = new Map<string, MemberInvitation>();
        invResp.invitations.forEach((inv: MemberInvitation) => {
          invitationsMap.set(inv.una_member_id, inv);
        });
        setInvitations(invitationsMap);
      }
    } catch (error) {
      console.error("Error loading members:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load UNA members",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMember = async () => {
    try {
      if (!formData.full_name || !formData.email) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Name and email are required",
        });
        return;
      }

      if (!unaId) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "No UNA found",
        });
        return;
      }

      const { data: existingUser } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", formData.email)
        .single();

      const memberData: any = {
        una_id: unaId,
        full_name: formData.full_name,
        email: formData.email || null,
        phone: formData.phone || null,
        role: formData.role,
        status: formData.status,
        member_role: formData.member_role,
        user_id: existingUser?.id || null,
      };

      if (editingMember) {
        const { error } = await supabase
          .from("una_members")
          .update(memberData)
          .eq("id", editingMember.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Member updated successfully",
        });

        await logAuditAction(unaId, "member_updated", "member", editingMember.id, {
          member_name: formData.full_name,
          role: formData.member_role,
        });
      } else {
        const { error } = await supabase.from("una_members").insert([
          {
            ...memberData,
            joined_date: new Date().toISOString().split("T")[0],
          },
        ]);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Member added successfully",
        });

        await supabase
          .from("saved_assignees")
          .insert({
            user_id: user?.id,
            una_id: unaId,
            assignee_name: formData.full_name,
            email: formData.email,
            role: formData.role,
          })
          .then(({ error }) => {
            if (error && !error.message.includes("duplicate")) {
              console.warn("Could not add to saved_assignees:", error);
            }
          });

        await logAuditAction(unaId, "member_added", "member", undefined, {
          member_name: formData.full_name,
          role: formData.member_role,
        });
      }

      resetForm();
      if (unaId) await loadMembers(unaId);
    } catch (error: any) {
      console.error("Error saving member:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save member",
      });
    }
  };

  const handleDeleteMember = async (memberId: string) => {
    if (!confirm("Are you sure you want to remove this member?")) return;

    try {
      const { error } = await supabase.from("una_members").delete().eq("id", memberId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Member removed successfully",
      });

      if (unaId) {
        await logAuditAction(unaId, "member_removed", "member", memberId);
        await loadMembers(unaId);
      }
    } catch (error) {
      console.error("Error deleting member:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to remove member",
      });
    }
  };

  const openEditModal = (member: UnaMember) => {
    setFormData({
      full_name: member.full_name,
      email: member.email || "",
      phone: member.phone || "",
      role: member.role,
      status: member.status,
      member_role: member.member_role || "board_member_passive",
    });
    setEditingMember(member);
    setIsModalOpen(true);
  };

  const sendInvitation = async (member: UnaMember) => {
    if (!member.email || !unaId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Member must have an email address to send invitation",
      });
      return;
    }

    try {
      const token = crypto.randomUUID() + "-" + Date.now();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const { error: inviteError } = await supabase.from("member_invitations").insert({
        una_id: unaId,
        una_member_id: member.id,
        invited_by: user?.id,
        invitation_token: token,
        email: member.email,
        member_role: member.member_role,
        expires_at: expiresAt.toISOString(),
      });

      if (inviteError) throw inviteError;

      const { error: emailError } = await supabase.functions.invoke("send-member-invitation", {
        body: {
          email: member.email,
          memberName: member.full_name,
          unaName: unaName,
          role: member.member_role,
          invitationToken: token,
          baseUrl: window.location.origin,
        },
      });

      if (emailError) {
        console.error("Email error:", emailError);
        toast({
          title: "Invitation Created",
          description: "Invitation created but email failed to send. Please contact support.",
        });
      } else {
        toast({
          title: "Invitation Sent!",
          description: `Invitation email sent to ${member.email}`,
        });
      }

      await logAuditAction(unaId, "member_invited", "invitation", member.id, {
        member_name: member.full_name,
        member_email: member.email,
        role: member.member_role,
      });

      await loadMembers(unaId);
    } catch (error: any) {
      console.error("Error sending invitation:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to send invitation: " + error.message,
      });
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: "",
      email: "",
      phone: "",
      role: "",
      status: "active",
      member_role: "board_member_passive" as MemberRole,
    });
    setEditingMember(null);
    setIsModalOpen(false);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold">UNA Members</h1>
            </div>
            <p className="text-muted-foreground mt-1">
              Manage your UNA membership roster and access permissions
            </p>
          </div>
          {MEMBER_APPLICATIONS_ENABLED && (
            <Button variant="outline" asChild className="min-h-11">
              <a href="/una-members/applications">Member applications</a>
            </Button>
          )}
        </div>


        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Active Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {members.filter((m) => m.status === "active").length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Board Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {
                  members.filter(
                    (m) =>
                      m.member_role === "board_member_active" ||
                      m.member_role === "board_member_passive"
                  ).length
                }
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{members.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Members Table - Desktop */}
        <Card className="hidden md:block">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role / Access Level
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y">
                  {members.map((member) => (
                    <tr key={member.id} className="hover:bg-muted/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium">{member.full_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {member.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                        {member.phone || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <div className="text-sm text-gray-900">{member.role}</div>
                          <RoleBadge
                            role={member.member_role}
                            size="small"
                            showDescription
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            member.status === "active"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {member.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {member.user_id ? (
                          <div className="space-y-1">
                            <Badge variant="secondary" className="flex items-center gap-1 w-fit bg-green-100 text-green-800">
                              <CheckCircle2 className="h-3 w-3" />
                              Accepted
                            </Badge>
                            {member.last_login && (
                              <div className="text-xs text-muted-foreground">
                                Last: {format(new Date(member.last_login), "MMM d, h:mm a")}
                              </div>
                            )}
                            {member.login_count > 0 && (
                              <div className="text-xs text-muted-foreground">
                                {member.login_count} logins
                              </div>
                            )}
                          </div>
                        ) : member.onboarded_via_workflow ? (
                          <Badge variant="secondary" className="flex items-center gap-1 w-fit bg-green-100 text-green-800">
                            <CheckCircle2 className="h-3 w-3" />
                            Onboarded
                          </Badge>
                        ) : invitations.has(member.id) && invitations.get(member.id)?.status === 'pending' ? (
                          <div className="space-y-1">
                            <Badge variant="default" className="flex items-center gap-1 w-fit bg-blue-100 text-blue-800 border-blue-300">
                              <Mail className="h-3 w-3" />
                              Invited, Not Accepted
                            </Badge>
                            <div className="text-xs text-muted-foreground">
                              Expires: {format(new Date(invitations.get(member.id)!.expires_at), "MMM d, yyyy")}
                            </div>
                          </div>
                        ) : (
                          <Badge variant="outline">Not Invited</Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(member)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          {!member.user_id && !member.onboarded_via_workflow && member.email && !invitations.has(member.id) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => sendInvitation(member)}
                            >
                              <Mail className="h-4 w-4 mr-1" />
                              Send Invitation
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteMember(member.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Members Cards - Mobile */}
        <div className="md:hidden space-y-4">
          {members.map((member) => (
            <Card key={member.id}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium">{member.full_name}</div>
                    <div className="text-sm text-muted-foreground">{member.email}</div>
                    {member.phone && (
                      <div className="text-sm text-muted-foreground">{member.phone}</div>
                    )}
                  </div>
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      member.status === "active"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {member.status}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">
                  {member.role && (
                    <Badge variant="outline" className="text-xs">{member.role}</Badge>
                  )}
                  <RoleBadge role={member.member_role} size="small" />
                </div>

                <div>
                  {member.user_id ? (
                    <Badge variant="secondary" className="flex items-center gap-1 w-fit bg-green-100 text-green-800">
                      <CheckCircle2 className="h-3 w-3" />
                      Accepted
                    </Badge>
                  ) : member.onboarded_via_workflow ? (
                    <Badge variant="secondary" className="flex items-center gap-1 w-fit bg-green-100 text-green-800">
                      <CheckCircle2 className="h-3 w-3" />
                      Onboarded
                    </Badge>
                  ) : invitations.has(member.id) && invitations.get(member.id)?.status === 'pending' ? (
                    <Badge variant="default" className="flex items-center gap-1 w-fit bg-blue-100 text-blue-800 border-blue-300">
                      <Mail className="h-3 w-3" />
                      Invited
                    </Badge>
                  ) : (
                    <Badge variant="outline">Not Invited</Badge>
                  )}
                </div>

                <div className="flex items-center gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 min-h-[44px]"
                    onClick={() => openEditModal(member)}
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  {!member.user_id && !member.onboarded_via_workflow && member.email && !invitations.has(member.id) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 min-h-[44px]"
                      onClick={() => sendInvitation(member)}
                    >
                      <Mail className="h-4 w-4 mr-1" />
                      Invite
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="min-h-[44px]"
                    onClick={() => handleDeleteMember(member.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Add/Edit Member Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingMember ? "Edit Member" : "Add New Member"}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Board Role</Label>
                <Input
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  placeholder="e.g., Board Member, Advisory Member"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="member_role">Access Level</Label>
                <Select
                  value={formData.member_role}
                  onValueChange={(value) =>
                    setFormData({ ...formData, member_role: value as MemberRole })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="board_member_passive">Passive Board Member</SelectItem>
                    <SelectItem value="board_member_active">Active Board Member</SelectItem>
                    <SelectItem value="managing_member">Managing Member</SelectItem>
                    <SelectItem value="financial_specialist">Financial Specialist</SelectItem>
                    <SelectItem value="legal_specialist">Legal Specialist</SelectItem>
                    <SelectItem value="tax_specialist">Tax Specialist</SelectItem>
                    <SelectItem value="operations_manager">Operations Manager</SelectItem>
                    <SelectItem value="advisor">Advisor</SelectItem>
                    <SelectItem value="observer">Observer</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  {getRoleDescription(formData.member_role)}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button onClick={handleSaveMember}>
                {editingMember ? "Update Member" : "Add Member"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
